<template>
  <div>
    <!-- Navigation Bar -->
    <nav class="navbar navbar-expand-lg navbar-dark bg-primary">
      <div class="container">
        <router-link class="navbar-brand fw-bold" to="/gallery">Design.AppVido</router-link>
        
        <div class="navbar-nav ms-auto d-flex align-items-center">
          <div class="nav-item dropdown">
            <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false" ref="dropdownToggle">
              {{ user.full_name }}
            </a>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><router-link class="dropdown-item" to="/gallery">Gallery</router-link></li>
              <li><router-link class="dropdown-item" to="/settings">Settings</router-link></li>
              <li><router-link class="dropdown-item" to="/admin">Admin Panel</router-link></li>
              <li><hr class="dropdown-divider"></li>
              <li><a class="dropdown-item" href="#" @click="logout">Logout</a></li>
            </ul>
          </div>
        </div>
      </div>
    </nav>

    <div class="container mt-4">
      <div class="row">
        <!-- Sidebar -->
        <div class="col-md-3">
          <div class="card">
            <div class="card-header bg-primary text-white">
              <h5 class="mb-0">Admin Panel</h5>
            </div>
            <div class="list-group list-group-flush">
              <button 
                class="list-group-item list-group-item-action"
                :class="{ active: activeTab === 'users' }"
                @click="setActiveTab('users')"
              >
                <i class="fas fa-users me-2"></i>Users
              </button>
              <button 
                class="list-group-item list-group-item-action"
                :class="{ active: activeTab === 'sessions' }"
                @click="setActiveTab('sessions')"
              >
                <i class="fas fa-clock me-2"></i>Active Sessions
              </button>
              <button 
                class="list-group-item list-group-item-action"
                :class="{ active: activeTab === 'categories' }"
                @click="setActiveTab('categories')"
              >
                <i class="fas fa-tags me-2"></i>Categories
              </button>
              <button 
                class="list-group-item list-group-item-action"
                :class="{ active: activeTab === 'gallery' }"
                @click="setActiveTab('gallery')"
              >
                <i class="fas fa-images me-2"></i>Gallery
              </button>
              <button 
                class="list-group-item list-group-item-action"
                :class="{ active: activeTab === 'effects' }"
                @click="setActiveTab('effects')"
              >
                <i class="fas fa-magic me-2"></i>Slideshow Effects
              </button>
              <button 
                class="list-group-item list-group-item-action"
                :class="{ active: activeTab === 'slideshow-settings' }"
                @click="setActiveTab('slideshow-settings')"
              >
                <i class="fas fa-cog me-2"></i>Slideshow Settings
              </button>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="col-md-9">
          <!-- Users Management -->
          <div v-if="activeTab === 'users'">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h3>Users Management</h3>
              <button class="btn btn-primary" @click="showCreateUser = true">
                Create User
              </button>
            </div>
            
            <!-- Create User Form -->
            <div v-if="showCreateUser" class="card mb-4">
              <div class="card-body">
                <h5>Create New User</h5>
                <form @submit.prevent="createUser">
                  <div class="mb-3">
                    <label class="form-label">Full Name</label>
                    <input type="text" class="form-control" v-model="newUser.full_name" required>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Email</label>
                    <input type="email" class="form-control" v-model="newUser.email" required>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Phone</label>
                    <input type="text" class="form-control" v-model="newUser.phone">
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Password</label>
                    <input type="password" class="form-control" v-model="newUser.password" required>
                  </div>
                  <div class="mb-3">
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" v-model="newUser.is_admin" id="newUserAdmin">
                      <label class="form-check-label" for="newUserAdmin">
                        Admin User
                      </label>
                    </div>
                  </div>
                  <button type="submit" class="btn btn-success me-2" :disabled="userLoading">
                    <span v-if="userLoading" class="spinner-border spinner-border-sm me-2"></span>
                    Create
                  </button>
                  <button type="button" class="btn btn-secondary" @click="cancelCreateUser">
                    Cancel
                  </button>
                </form>
              </div>
            </div>
            
            <!-- Edit User Modal -->
            <div v-if="showEditUser" class="modal d-block" tabindex="-1">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Edit User</h5>
                    <button type="button" class="btn-close" @click="cancelEditUser"></button>
                  </div>
                  <div class="modal-body">
                    <form @submit.prevent="updateUser">
                      <div class="mb-3">
                        <label class="form-label">Full Name</label>
                        <input type="text" class="form-control" v-model="editingUser.full_name" required>
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Email</label>
                        <input type="email" class="form-control" v-model="editingUser.email" required>
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Phone</label>
                        <input type="text" class="form-control" v-model="editingUser.phone">
                      </div>
                      <div class="mb-3">
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="editingUser.is_admin" id="editUserAdmin">
                          <label class="form-check-label" for="editUserAdmin">
                            Admin User
                          </label>
                        </div>
                      </div>
                    </form>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="cancelEditUser">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="updateUser" :disabled="userLoading">
                      <span v-if="userLoading" class="spinner-border spinner-border-sm me-2"></span>
                      Update User
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="showEditUser" class="modal-backdrop show"></div>

            <!-- Change Password Modal -->
            <div v-if="showChangePassword" class="modal d-block" tabindex="-1">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Change Password</h5>
                    <button type="button" class="btn-close" @click="cancelChangePassword"></button>
                  </div>
                  <div class="modal-body">
                    <div class="alert alert-info">
                      <i class="fas fa-info-circle me-2"></i>
                      You are changing the password for <strong>{{ passwordChangeUser.full_name }}</strong> ({{ passwordChangeUser.email }}).
                      The user will be logged out from all sessions and will need to log in again.
                    </div>
                    <form @submit.prevent="changeUserPassword">
                      <div class="mb-3">
                        <label class="form-label">New Password</label>
                        <input 
                          type="password" 
                          class="form-control" 
                          v-model="newPasswordData.new_password" 
                          required
                          minlength="6"
                          placeholder="Enter new password"
                        >
                        <small class="form-text text-muted">Password must be at least 6 characters long</small>
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Confirm New Password</label>
                        <input 
                          type="password" 
                          class="form-control" 
                          v-model="confirmPassword" 
                          required
                          placeholder="Confirm new password"
                        >
                      </div>
                    </form>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="cancelChangePassword">Cancel</button>
                    <button 
                      type="button" 
                      class="btn btn-warning" 
                      @click="changeUserPassword" 
                      :disabled="passwordLoading || newPasswordData.new_password !== confirmPassword || newPasswordData.new_password.length < 6"
                    >
                      <span v-if="passwordLoading" class="spinner-border spinner-border-sm me-2"></span>
                      Change Password
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="showChangePassword" class="modal-backdrop show"></div>
            
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
            </div>
            
            <div v-else class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in users" :key="user.id">
                    <td>{{ user.id }}</td>
                    <td>{{ user.full_name }}</td>
                    <td>{{ user.email }}</td>
                    <td>{{ user.phone || '-' }}</td>
                    <td>
                      <span :class="['badge', user.is_admin ? 'bg-danger' : 'bg-success']">
                        {{ user.is_admin ? 'Admin' : 'User' }}
                      </span>
                    </td>
                    <td>{{ formatDate(user.created_at) }}</td>
                    <td>
                      <button 
                        class="btn btn-sm btn-outline-primary me-2"
                        @click="editUser(user)"
                      >
                        Edit
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-warning me-2"
                        @click="showChangePasswordModal(user)"
                        :title="`Change password for ${user.full_name}`"
                      >
                        <i class="fas fa-key"></i>
                      </button>
                      <button 
                        v-if="user.id !== currentUser.id"
                        class="btn btn-sm btn-outline-danger"
                        @click="deleteUser(user)"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Active Sessions Management -->
          <div v-if="activeTab === 'sessions'">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h3>Active Sessions</h3>
              <button class="btn btn-outline-primary" @click="loadSessions">
                Refresh
              </button>
            </div>
            
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
            </div>
            
            <div v-else>
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Login Date</th>
                    <th>Last Activity</th>
                    <th>User Agent</th>
                    <th>IP Address</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="session in sessions" :key="session.id">
                    <td>{{ session.user_name }}</td>
                    <td>{{ session.user_email }}</td>
                    <td>{{ formatDate(session.login_date) }}</td>
                    <td>{{ formatDate(session.last_activity) }}</td>
                    <td>
                      <span class="text-muted" :title="session.user_agent">
                        {{ session.user_agent ? session.user_agent.substring(0, 30) + '...' : '-' }}
                      </span>
                    </td>
                    <td>{{ session.ip_address || '-' }}</td>
                    <td>
                      <button 
                        class="btn btn-sm btn-outline-danger me-2"
                        @click="logoutSession(session)"
                        :disabled="sessionLoading"
                      >
                        Logout
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-warning"
                        @click="logoutAllUserSessions(session.user_id, session.user_name)"
                        :disabled="sessionLoading"
                      >
                        Logout All
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <div v-if="sessions.length === 0" class="text-center py-4 text-muted">
                No active sessions found
              </div>
            </div>
          </div>

          <!-- Categories Management -->
          <div v-if="activeTab === 'categories'">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h3>Categories Management</h3>
              <button class="btn btn-primary" @click="showCreateCategory = true">
                Add Category
              </button>
            </div>
            
            <!-- Create Category Form -->
            <div v-if="showCreateCategory" class="card mb-4">
              <div class="card-body">
                <h5>Create New Category</h5>
                <form @submit.prevent="createCategory">
                  <div class="mb-3">
                    <label class="form-label">Category Name</label>
                    <input type="text" class="form-control" v-model="newCategory.name" required>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Description</label>
                    <textarea class="form-control" v-model="newCategory.description" rows="3"></textarea>
                  </div>
                  <button type="submit" class="btn btn-success me-2" :disabled="categoryLoading">
                    <span v-if="categoryLoading" class="spinner-border spinner-border-sm me-2"></span>
                    Create
                  </button>
                  <button type="button" class="btn btn-secondary" @click="cancelCreateCategory">
                    Cancel
                  </button>
                </form>
              </div>
            </div>
            
            <!-- Edit Category Modal -->
            <div v-if="showEditCategory" class="modal d-block" tabindex="-1">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Edit Category</h5>
                    <button type="button" class="btn-close" @click="cancelEditCategory"></button>
                  </div>
                  <div class="modal-body">
                    <form @submit.prevent="updateCategory">
                      <div class="mb-3">
                        <label class="form-label">Category Name</label>
                        <input type="text" class="form-control" v-model="editingCategory.name" required>
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Description</label>
                        <textarea class="form-control" v-model="editingCategory.description" rows="3"></textarea>
                      </div>
                    </form>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="cancelEditCategory">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="updateCategory" :disabled="categoryLoading">
                      <span v-if="categoryLoading" class="spinner-border spinner-border-sm me-2"></span>
                      Update Category
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="showEditCategory" class="modal-backdrop show"></div>
            
            <div class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="category in categories" :key="category.id">
                    <td>{{ category.id }}</td>
                    <td>{{ category.name }}</td>
                    <td>{{ category.description || '-' }}</td>
                    <td>{{ formatDate(category.created_at) }}</td>
                    <td>
                      <button 
                        class="btn btn-sm btn-outline-primary me-2"
                        @click="editCategory(category)"
                      >
                        Edit
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-danger"
                        @click="deleteCategory(category)"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Gallery Management -->
          <div v-if="activeTab === 'gallery'">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h3>Gallery Management</h3>
              <button class="btn btn-primary" @click="openCreateGallery">
                Add Photo
              </button>
            </div>
            
            <!-- Gallery Controls -->
            <div class="row mb-4">
              <div class="col-md-6 col-12 mb-3 mb-md-0">
                <label class="form-label">Filter by Category</label>
                <select 
                  class="form-select" 
                  v-model="selectedGalleryCategory"
                  @change="filterGalleryByCategory"
                >
                  <option value="">All Categories</option>
                  <option v-for="category in categories" :key="category.id" :value="category.id">
                    {{ category.name }}
                  </option>
                </select>
              </div>
              
              <div class="col-md-6 col-12">
                <label class="form-label">Bulk Actions</label>
                <div class="d-flex gap-2">
                  <button 
                    class="btn btn-outline-secondary btn-sm"
                    @click="toggleSelectAll"
                    :disabled="galleryItems.length === 0"
                  >
                    {{ isAllSelected ? 'Deselect All' : 'Select All' }}
                  </button>
                  <button 
                    class="btn btn-danger btn-sm"
                    @click="bulkDeleteGallery"
                    :disabled="selectedGalleryItems.size === 0 || galleryLoading"
                  >
                    <span v-if="galleryLoading" class="spinner-border spinner-border-sm me-2"></span>
                    Delete Selected ({{ selectedGalleryItems.size }})
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Upload Photo Form -->
            <div v-if="showCreateGallery" class="card mb-4">
              <div class="card-body">
                <h5>Upload New Photo</h5>
                <form @submit.prevent="uploadPhoto">
                  <div class="mb-3">
                    <label class="form-label">Title</label>
                    <input type="text" class="form-control" v-model="newGalleryItem.title" required>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Description</label>
                    <textarea class="form-control" v-model="newGalleryItem.description" rows="3"></textarea>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Category</label>
                    <select class="form-select" v-model="newGalleryItem.category_id" @change="saveSelectedCategory" required>
                      <option value="">Select Category</option>
                      <option v-for="category in categories" :key="category.id" :value="category.id">
                        {{ category.name }}
                      </option>
                    </select>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Photo</label>
                    <input type="file" class="form-control" @change="onFileSelect" accept="image/*" required>
                    <small class="form-text text-muted">Supported formats: JPG, PNG, GIF</small>
                  </div>
                  <button type="submit" class="btn btn-success me-2" :disabled="galleryLoading">
                    <span v-if="galleryLoading" class="spinner-border spinner-border-sm me-2"></span>
                    Upload
                  </button>
                  <button type="button" class="btn btn-secondary" @click="cancelCreateGallery">
                    Cancel
                  </button>
                </form>
              </div>
            </div>

            <!-- Edit Gallery Modal -->
            <div v-if="showEditGallery" class="modal d-block" tabindex="-1">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Edit Photo</h5>
                    <button type="button" class="btn-close" @click="cancelEditGallery"></button>
                  </div>
                  <div class="modal-body">
                    <form @submit.prevent="updateGallery">
                      <div class="mb-3">
                        <label class="form-label">Title</label>
                        <input type="text" class="form-control" v-model="editingGallery.title" required>
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Description</label>
                        <textarea class="form-control" v-model="editingGallery.description" rows="3"></textarea>
                      </div>
                    </form>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="cancelEditGallery">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="updateGallery" :disabled="galleryLoading">
                      <span v-if="galleryLoading" class="spinner-border spinner-border-sm me-2"></span>
                      Update Photo
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="showEditGallery" class="modal-backdrop show"></div>
            
            <!-- Pagination Info -->
            <div v-if="galleryPagination.total > 0" class="d-flex justify-content-between align-items-center mb-3">
              <small class="text-muted">
                Showing {{ ((galleryPagination.page - 1) * galleryPagination.limit) + 1 }} to 
                {{ Math.min(galleryPagination.page * galleryPagination.limit, galleryPagination.total) }} 
                of {{ galleryPagination.total }} photos
              </small>
              <div class="btn-group">
                <button 
                  class="btn btn-outline-secondary btn-sm"
                  :disabled="!galleryPagination.has_prev || loading"
                  @click="prevGalleryPage"
                >
                  Previous
                </button>
                <button 
                  class="btn btn-outline-secondary btn-sm"
                  :disabled="!galleryPagination.has_next || loading"
                  @click="nextGalleryPage"
                >
                  Next
                </button>
              </div>
            </div>
            
            <div class="row">
              <div v-for="item in galleryItems" :key="item.id" class="col-md-4 mb-3">
                <div class="card h-100" :class="{ 'border-primary': selectedGalleryItems.has(item.id) }">
                  <!-- Selection checkbox -->
                  <div class="position-absolute top-0 start-0 p-2" style="z-index: 10;">
                    <input 
                      type="checkbox" 
                      class="form-check-input" 
                      :checked="selectedGalleryItems.has(item.id)"
                      @change="toggleGalleryItemSelection(item.id)"
                    >
                  </div>
                  
                  <img :src="item.image_url" class="card-img-top" style="height: 200px; object-fit: cover;" :alt="item.title">
                  <div class="card-body d-flex flex-column">
                    <h6 class="card-title">{{ item.title }}</h6>
                    <p class="card-text text-muted small description-truncated">{{ item.description }}</p>
                    <span class="badge bg-secondary mb-2">{{ item.category_name }}</span>
                    <div class="text-muted small mb-2">
                      <div><strong>Created:</strong> {{ formatDate(item.created_at) }}</div>
                      <div><strong>Updated:</strong> {{ formatDate(item.updated_at) }}</div>
                    </div>
                    <div class="d-flex gap-2 mt-auto">
                      <button 
                        class="btn btn-sm btn-outline-primary"
                        @click="editGallery(item)"
                        :disabled="galleryLoading"
                      >
                        Edit
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-danger"
                        @click="deleteGallery(item)"
                        :disabled="galleryLoading"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination Controls -->
            <div v-if="galleryPagination.total_pages > 1" class="d-flex justify-content-center mt-4">
              <nav>
                <ul class="pagination">
                  <li class="page-item" :class="{ disabled: !galleryPagination.has_prev || loading }">
                    <button class="page-link" @click="prevGalleryPage" :disabled="!galleryPagination.has_prev || loading">
                      Previous
                    </button>
                  </li>
                  
                  <!-- Page numbers -->
                  <li 
                    v-for="pageNum in getVisiblePages()" 
                    :key="pageNum" 
                    class="page-item" 
                    :class="{ active: pageNum === galleryPagination.page }"
                  >
                    <button 
                      class="page-link" 
                      @click="goToGalleryPage(pageNum)"
                      :disabled="loading"
                    >
                      {{ pageNum }}
                    </button>
                  </li>
                  
                  <li class="page-item" :class="{ disabled: !galleryPagination.has_next || loading }">
                    <button class="page-link" @click="nextGalleryPage" :disabled="!galleryPagination.has_next || loading">
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>

            <!-- Empty state -->
            <div v-if="galleryItems.length === 0 && !loading" class="text-center py-5">
              <div class="text-muted">
                <i class="fas fa-images" style="font-size: 3rem;"></i>
                <h5 class="mt-3">No photos found</h5>
                <p>Upload your first photo to get started</p>
              </div>
            </div>
          </div>

          <!-- Slideshow Effects Management -->
          <div v-if="activeTab === 'effects'">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h3>Slideshow Effects</h3>
              <button class="btn btn-primary" @click="showCreateEffect = true">
                Add Effect
              </button>
            </div>
            
            <!-- Create Effect Form -->
            <div v-if="showCreateEffect" class="card mb-4">
              <div class="card-body">
                <h5>Create New Effect</h5>
                <form @submit.prevent="createEffect">
                  <div class="mb-3">
                    <label class="form-label">Effect Name</label>
                    <input type="text" class="form-control" v-model="newEffect.name" required>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">CSS Class</label>
                    <input type="text" class="form-control" v-model="newEffect.css_class" required>
                    <small class="form-text text-muted">CSS class name for the transition effect</small>
                  </div>
                  <button type="submit" class="btn btn-success me-2" :disabled="effectLoading">
                    <span v-if="effectLoading" class="spinner-border spinner-border-sm me-2"></span>
                    Create
                  </button>
                  <button type="button" class="btn btn-secondary" @click="cancelCreateEffect">
                    Cancel
                  </button>
                </form>
              </div>
            </div>
            
            <div class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>CSS Class</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="effect in slideshowEffects" :key="effect.id">
                    <td>{{ effect.id }}</td>
                    <td>{{ effect.name }}</td>
                    <td><code>{{ effect.css_class }}</code></td>
                    <td>
                      <span :class="['badge', effect.is_active ? 'bg-success' : 'bg-secondary']">
                        {{ effect.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td>{{ formatDate(effect.created_at) }}</td>
                    <td>
                      <button 
                        v-if="!effect.is_active"
                        class="btn btn-sm btn-success"
                        @click="activateEffect(effect)"
                        :disabled="effectLoading"
                      >
                        <span v-if="effectLoading" class="spinner-border spinner-border-sm me-2"></span>
                        Activate
                      </button>
                      <span v-else class="text-success fw-bold">
                        <i class="fas fa-check-circle"></i> Currently Active
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <!-- Slideshow Settings -->
          <div v-if="activeTab === 'slideshow-settings'">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h3>Slideshow Settings</h3>
            </div>
            
            <div class="card">
              <div class="card-body">
                <h5>Auto-advance Settings</h5>
                <form @submit.prevent="updateSlideshowSettings">
                  <div class="mb-3">
                    <label class="form-label">Display Duration (seconds)</label>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model.number="slideshowSettings.interval" 
                      min="1" 
                      max="60" 
                      required
                    >
                    <small class="form-text text-muted">
                      How long each photo is displayed before automatically advancing to the next
                    </small>
                  </div>
                  
                  <button type="submit" class="btn btn-success" :disabled="settingsLoading">
                    <span v-if="settingsLoading" class="spinner-border spinner-border-sm me-2"></span>
                    Update Settings
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Dropdown } from 'bootstrap'

export default {
  name: 'AdminPanel',
  data() {
    return {
      user: {},
      currentUser: {},
      activeTab: 'users',
      loading: false,
      
      // Users
      users: [],
      showCreateUser: false,
      newUser: { full_name: '', email: '', phone: '', password: '', is_admin: false },
      showEditUser: false,
      editingUser: { id: null, full_name: '', email: '', phone: '', is_admin: false },
      userLoading: false,
      
      // Password change
      showChangePassword: false,
      passwordChangeUser: { id: null, full_name: '', email: '' },
      newPasswordData: { new_password: '' },
      confirmPassword: '',
      passwordLoading: false,
      
      // Sessions
      sessions: [],
      sessionLoading: false,
      
      // Categories
      categories: [],
      showCreateCategory: false,
      newCategory: { name: '', description: '' },
      showEditCategory: false,
      editingCategory: { id: null, name: '', description: '' },
      categoryLoading: false,
      
      // Gallery
      galleryItems: [],
      galleryPagination: {
        page: 1,
        limit: 50,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      },
      selectedGalleryCategory: '', // Filter for gallery by category
      selectedGalleryItems: new Set(), // Set of selected gallery item IDs
      showCreateGallery: false,
      newGalleryItem: { title: '', description: '', category_id: '' },
      selectedFile: null,
      galleryLoading: false,
      showEditGallery: false,
      editingGallery: { id: null, title: '', description: '' },
      
      // Effects
      slideshowEffects: [],
      showCreateEffect: false,
      newEffect: { name: '', css_class: '' },
      effectLoading: false,
      
      // Slideshow Settings
      slideshowSettings: { interval: 5 },
      settingsLoading: false
    }
  },
  computed: {
    isAllSelected() {
      return this.galleryItems.length > 0 && this.selectedGalleryItems.size === this.galleryItems.length
    }
  },
  methods: {
    setActiveTab(tab) {
      this.activeTab = tab
      // Reset gallery pagination when switching to gallery tab
      if (tab === 'gallery') {
        this.galleryPagination.page = 1
        this.selectedGalleryItems.clear() // Clear selections when switching tabs
      }
      this.loadTabData()
    },
    
    async loadTabData() {
      this.loading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        switch (this.activeTab) {
          case 'users': {
            const usersResponse = await axios.get('https://designappvido-backend.up.railway.app/api/admin/users', config)
            this.users = usersResponse.data
            break
          }
          case 'sessions': {
            const sessionsResponse = await axios.get('https://designappvido-backend.up.railway.app/api/admin/sessions', config)
            this.sessions = sessionsResponse.data
            break
          }
          case 'categories': {
            const categoriesResponse = await axios.get('https://designappvido-backend.up.railway.app/api/categories')
            this.categories = categoriesResponse.data
            break
          }
          case 'gallery': {
            const [catResponse, galleryResponse] = await Promise.all([
              axios.get('https://designappvido-backend.up.railway.app/api/categories'),
              this.loadGalleryData()
            ])
            this.categories = catResponse.data
            if (galleryResponse.items) {
              this.galleryItems = galleryResponse.items
              this.galleryPagination = galleryResponse.pagination
            } else {
              this.galleryItems = galleryResponse
              this.galleryPagination.has_next = false
            }
            break
          }
          case 'effects': {
            const effectsResponse = await axios.get('https://designappvido-backend.up.railway.app/api/slideshow-effects')
            this.slideshowEffects = effectsResponse.data
            break
          }
          case 'slideshow-settings': {
            const settingsResponse = await axios.get('https://designappvido-backend.up.railway.app/api/slideshow-settings', config)
            this.slideshowSettings = settingsResponse.data
            break
          }
        }
      } catch (error) {
        console.error('Failed to load data:', error)
        if (error.response?.status === 401) {
          this.logout()
        }
      } finally {
        this.loading = false
      }
    },
    
    async loadGalleryData() {
      const token = localStorage.getItem('token')
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      }
      
      const params = {
        page: this.galleryPagination.page,
        limit: this.galleryPagination.limit
      }
      
      if (this.selectedGalleryCategory) {
        params.category_id = this.selectedGalleryCategory
      }
      
      const response = await axios.get('https://designappvido-backend.up.railway.app/api/gallery', { ...config, params })
      return response.data
    },
    
    async filterGalleryByCategory() {
      // Reset pagination when filtering
      this.galleryPagination.page = 1
      this.selectedGalleryItems.clear() // Clear selections when filtering
      await this.loadTabData()
    },
    
    toggleGalleryItemSelection(itemId) {
      if (this.selectedGalleryItems.has(itemId)) {
        this.selectedGalleryItems.delete(itemId)
      } else {
        this.selectedGalleryItems.add(itemId)
      }
    },
    
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedGalleryItems.clear()
      } else {
        this.galleryItems.forEach(item => {
          this.selectedGalleryItems.add(item.id)
        })
      }
    },
    
    async bulkDeleteGallery() {
      const selectedIds = Array.from(this.selectedGalleryItems)
      if (selectedIds.length === 0) {
        return
      }
      
      if (!confirm(`Are you sure you want to delete ${selectedIds.length} selected photos? This will also delete the images from Cloudinary.`)) {
        return
      }
      
      this.galleryLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        const response = await axios.post('https://designappvido-backend.up.railway.app/api/admin/gallery/bulk-delete', selectedIds, config)
        
        // Clear selections and reload data
        this.selectedGalleryItems.clear()
        await this.loadTabData()
        
        // Show success message
        alert(response.data.message)
        
        // Show warning if there were Cloudinary errors
        if (response.data.cloudinary_errors) {
          console.warn('Cloudinary deletion errors:', response.data.cloudinary_errors)
        }
      } catch (error) {
        alert('Failed to delete gallery items: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.galleryLoading = false
      }
    },
    
    async deleteUser(user) {
      if (!confirm(`Are you sure you want to delete user "${user.full_name}"?`)) {
        return
      }
      
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.delete(`https://designappvido-backend.up.railway.app/api/admin/users/${user.id}`, config)
        this.users = this.users.filter(u => u.id !== user.id)
      } catch (error) {
        alert('Failed to delete user: ' + (error.response?.data?.detail || error.message))
      }
    },

    async createUser() {
      this.userLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.post('https://designappvido-backend.up.railway.app/api/admin/users', this.newUser, config)
        this.cancelCreateUser()
        this.loadTabData()
      } catch (error) {
        alert('Failed to create user: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.userLoading = false
      }
    },
    
    cancelCreateUser() {
      this.showCreateUser = false
      this.newUser = { full_name: '', email: '', phone: '', password: '', is_admin: false }
    },

    editUser(user) {
      this.editingUser = { ...user }
      this.showEditUser = true
    },

    async updateUser() {
      this.userLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        const updateData = {
          full_name: this.editingUser.full_name,
          email: this.editingUser.email,
          phone: this.editingUser.phone,
          is_admin: this.editingUser.is_admin
        }
        
        await axios.put(`https://designappvido-backend.up.railway.app/api/admin/users/${this.editingUser.id}`, updateData, config)
        this.cancelEditUser()
        this.loadTabData()
      } catch (error) {
        alert('Failed to update user: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.userLoading = false
      }
    },

    cancelEditUser() {
      this.showEditUser = false
      this.editingUser = { id: null, full_name: '', email: '', phone: '', is_admin: false }
    },

    showChangePasswordModal(user) {
      this.passwordChangeUser = { 
        id: user.id, 
        full_name: user.full_name, 
        email: user.email 
      }
      this.newPasswordData = { new_password: '' }
      this.confirmPassword = ''
      this.showChangePassword = true
    },

    async changeUserPassword() {
      if (this.newPasswordData.new_password !== this.confirmPassword) {
        alert('Passwords do not match')
        return
      }
      
      if (this.newPasswordData.new_password.length < 6) {
        alert('Password must be at least 6 characters long')
        return
      }

      if (!confirm(`Are you sure you want to change the password for ${this.passwordChangeUser.full_name}? They will be logged out from all sessions.`)) {
        return
      }

      this.passwordLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        const response = await axios.post(
          `https://designappvido-backend.up.railway.app/api/admin/users/${this.passwordChangeUser.id}/change-password`,
          this.newPasswordData,
          config
        )
        
        alert(response.data.message)
        this.cancelChangePassword()
        
        // Refresh active sessions if we're on that tab
        if (this.activeTab === 'sessions') {
          this.loadSessions()
        }
      } catch (error) {
        alert('Failed to change password: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.passwordLoading = false
      }
    },

    cancelChangePassword() {
      this.showChangePassword = false
      this.passwordChangeUser = { id: null, full_name: '', email: '' }
      this.newPasswordData = { new_password: '' }
      this.confirmPassword = ''
    },
    
    async createCategory() {
      this.categoryLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.post('https://designappvido-backend.up.railway.app/api/admin/categories', this.newCategory, config)
        this.cancelCreateCategory()
        this.loadTabData()
      } catch (error) {
        alert('Failed to create category: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.categoryLoading = false
      }
    },
    
    cancelCreateCategory() {
      this.showCreateCategory = false
      this.newCategory = { name: '', description: '' }
    },

    editCategory(category) {
      this.editingCategory = { ...category }
      this.showEditCategory = true
    },

    async updateCategory() {
      this.categoryLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        const updateData = {
          name: this.editingCategory.name,
          description: this.editingCategory.description
        }
        
        await axios.put(`https://designappvido-backend.up.railway.app/api/admin/categories/${this.editingCategory.id}`, updateData, config)
        this.cancelEditCategory()
        this.loadTabData()
      } catch (error) {
        alert('Failed to update category: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.categoryLoading = false
      }
    },

    cancelEditCategory() {
      this.showEditCategory = false
      this.editingCategory = { id: null, name: '', description: '' }
    },

    async deleteCategory(category) {
      if (!confirm(`Are you sure you want to delete category "${category.name}"? This will also delete all photos in this category.`)) {
        return
      }
      
      this.categoryLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.delete(`https://designappvido-backend.up.railway.app/api/admin/categories/${category.id}`, config)
        this.loadTabData()
      } catch (error) {
        alert('Failed to delete category: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.categoryLoading = false
      }
    },
    
    async loadSessions() {
      this.loading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        const response = await axios.get('https://designappvido-backend.up.railway.app/api/admin/sessions', config)
        this.sessions = response.data
      } catch (error) {
        console.error('Failed to load sessions:', error)
        if (error.response?.status === 401) {
          this.logout()
        }
      } finally {
        this.loading = false
      }
    },
    
    async logoutSession(session) {
      if (!confirm(`Logout session for ${session.user_name}?`)) {
        return
      }
      
      this.sessionLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.post(`https://designappvido-backend.up.railway.app/api/admin/sessions/${session.id}/logout`, {}, config)
        this.loadSessions()
      } catch (error) {
        alert('Failed to logout session: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.sessionLoading = false
      }
    },
    
    async logoutAllUserSessions(userId, userName) {
      if (!confirm(`Logout ALL sessions for ${userName}?`)) {
        return
      }
      
      this.sessionLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.post(`https://designappvido-backend.up.railway.app/api/admin/users/${userId}/logout-all`, {}, config)
        this.loadSessions()
      } catch (error) {
        alert('Failed to logout user sessions: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.sessionLoading = false
      }
    },

    onFileSelect(event) {
      this.selectedFile = event.target.files[0]
    },
    
    async uploadPhoto() {
      if (!this.selectedFile) {
        alert('Please select a file')
        return
      }
      
      this.galleryLoading = true
      try {
        const token = localStorage.getItem('token')
        const formData = new FormData()
        formData.append('file', this.selectedFile)
        formData.append('title', this.newGalleryItem.title)
        formData.append('description', this.newGalleryItem.description)
        formData.append('category_id', this.newGalleryItem.category_id)
        
        const config = {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
        
        await axios.post('https://designappvido-backend.up.railway.app/api/admin/gallery', formData, config)
        this.cancelCreateGallery()
        this.loadTabData()
      } catch (error) {
        alert('Failed to upload photo: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.galleryLoading = false
      }
    },
    
    openCreateGallery() {
      this.showCreateGallery = true
      // Restore last selected category from localStorage
      const savedCategory = localStorage.getItem('lastSelectedGalleryCategory')
      if (savedCategory && this.categories.some(cat => cat.id == savedCategory)) {
        this.newGalleryItem.category_id = savedCategory
      }
    },
    
    saveSelectedCategory() {
      // Save selected category to localStorage when user changes it
      if (this.newGalleryItem.category_id) {
        localStorage.setItem('lastSelectedGalleryCategory', this.newGalleryItem.category_id)
      }
    },
    
    cancelCreateGallery() {
      this.showCreateGallery = false
      // Only reset title and description, preserve the saved category
      const savedCategory = localStorage.getItem('lastSelectedGalleryCategory')
      this.newGalleryItem = { 
        title: '', 
        description: '', 
        category_id: savedCategory && this.categories.some(cat => cat.id == savedCategory) ? savedCategory : '' 
      }
      this.selectedFile = null
    },

    // Gallery pagination methods
    async goToGalleryPage(page) {
      this.galleryPagination.page = page
      await this.loadTabData()
    },

    async nextGalleryPage() {
      if (this.galleryPagination.has_next) {
        await this.goToGalleryPage(this.galleryPagination.page + 1)
      }
    },

    async prevGalleryPage() {
      if (this.galleryPagination.has_prev) {
        await this.goToGalleryPage(this.galleryPagination.page - 1)
      }
    },

    // Gallery edit methods
    editGallery(item) {
      this.editingGallery = {
        id: item.id,
        title: item.title,
        description: item.description || ''
      }
      this.showEditGallery = true
    },

    async updateGallery() {
      this.galleryLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        const updateData = {
          title: this.editingGallery.title,
          description: this.editingGallery.description
        }
        
        await axios.put(`https://designappvido-backend.up.railway.app/api/admin/gallery/${this.editingGallery.id}`, updateData, config)
        this.cancelEditGallery()
        this.loadTabData()
      } catch (error) {
        alert('Failed to update gallery item: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.galleryLoading = false
      }
    },

    cancelEditGallery() {
      this.showEditGallery = false
      this.editingGallery = { id: null, title: '', description: '' }
    },

    // Gallery delete method
    async deleteGallery(item) {
      if (!confirm(`Are you sure you want to delete "${item.title}"? This will also delete the image from Cloudinary.`)) {
        return
      }
      
      this.galleryLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.delete(`https://designappvido-backend.up.railway.app/api/admin/gallery/${item.id}`, config)
        this.loadTabData()
      } catch (error) {
        alert('Failed to delete gallery item: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.galleryLoading = false
      }
    },
    
    async createEffect() {
      this.effectLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.post('https://designappvido-backend.up.railway.app/api/admin/slideshow-effects', this.newEffect, config)
        this.cancelCreateEffect()
        this.loadTabData()
      } catch (error) {
        alert('Failed to create effect: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.effectLoading = false
      }
    },
    
    cancelCreateEffect() {
      this.showCreateEffect = false
      this.newEffect = { name: '', css_class: '' }
    },
    
    async activateEffect(effect) {
      if (!confirm(`Activate slideshow effect "${effect.name}"? This will deactivate the current active effect.`)) {
        return
      }
      
      this.effectLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.put(`https://designappvido-backend.up.railway.app/api/admin/slideshow-effects/${effect.id}/activate`, {}, config)
        this.loadTabData() // Reload to show updated active status
        alert('Slideshow effect activated successfully!')
      } catch (error) {
        alert('Failed to activate effect: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.effectLoading = false
      }
    },
    
    async updateSlideshowSettings() {
      this.settingsLoading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.put('https://designappvido-backend.up.railway.app/api/admin/slideshow-settings', this.slideshowSettings, config)
        alert('Slideshow settings updated successfully!')
      } catch (error) {
        alert('Failed to update slideshow settings: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.settingsLoading = false
      }
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString()
    },
    
    async logout() {
      try {
        const token = localStorage.getItem('token')
        if (token) {
          // Call backend logout endpoint
          await axios.post('https://designappvido-backend.up.railway.app/api/logout', {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
        }
      } catch (error) {
        console.error('Logout error:', error)
        // Continue with local logout even if backend call fails
      } finally {
        // Always clear local storage and redirect
        localStorage.removeItem('token')
        this.$router.push('/')
      }
    },

    getVisiblePages() {
      const totalPages = this.galleryPagination.total_pages
      const currentPage = this.galleryPagination.page
      const visiblePages = []
      
      // Show up to 5 page numbers around current page
      let start = Math.max(1, currentPage - 2)
      let end = Math.min(totalPages, currentPage + 2)
      
      // Adjust range if we're near the beginning or end
      if (end - start < 4) {
        if (start === 1) {
          end = Math.min(totalPages, start + 4)
        } else if (end === totalPages) {
          start = Math.max(1, end - 4)
        }
      }
      
      for (let i = start; i <= end; i++) {
        visiblePages.push(i)
      }
      
      return visiblePages
    }
  },
  
  async mounted() {
    // Token validation and admin status check are now handled by router guard
    // Just fetch user info since we know the token is valid and user is admin
    try {
      const token = localStorage.getItem('token')
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      }
      const response = await axios.get('https://designappvido-backend.up.railway.app/api/me', config)
      this.user = response.data
      this.currentUser = this.user
    } catch (error) {
      if (error.response?.status === 401) {
        // Fallback: if session becomes invalid after router guard check
        localStorage.removeItem('token')
        this.$router.push('/')
        return
      }
    }
    
    // Load initial data
    this.loadTabData()
    
    // Initialize Bootstrap dropdown manually
    this.$nextTick(() => {
      if (this.$refs.dropdownToggle) {
        new Dropdown(this.$refs.dropdownToggle)
      }
    })
  }
}
</script>

<style scoped>
.bg-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.list-group-item.active {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.card {
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.card-header.bg-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border-radius: 10px 10px 0 0 !important;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
}

.description-truncated {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 4.5em; /* Ensures consistent height for 3 lines */
}

/* Gallery selection styles */
.card.border-primary {
  border-width: 2px !important;
  box-shadow: 0 0 10px rgba(13, 110, 253, 0.3);
}

.card .form-check-input {
  width: 1.2em;
  height: 1.2em;
  background-color: rgba(255, 255, 255, 0.9);
  border: 2px solid #dee2e6;
  backdrop-filter: blur(4px);
}

.card .form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}
</style>